import React, { useEffect, useState } from 'react';
import { AcademicCapIcon } from '@heroicons/react/24/outline';
import { schoolsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import type { MyPlayerSchool, SchoolOption } from '../../types/school';
import { getErrorMessage } from '../../utils/errorMessage';

/**
 * Profil: a játékos maga, illetve a szülő a gyermekei iskoláját adhatja meg.
 * Ide küldi a klub a kikérőket, igazolásokat. Választáskor azonnal mentődik.
 */
const MySchoolsCard: React.FC = () => {
  const { showToast } = useToast();
  const [players, setPlayers] = useState<MyPlayerSchool[]>([]);
  const [options, setOptions] = useState<SchoolOption[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);

  useEffect(() => {
    Promise.all([schoolsAPI.getMyPlayers(), schoolsAPI.getOptions()])
      .then(([mine, schools]) => {
        setPlayers(mine);
        setOptions(schools);
      })
      .catch((err) => console.error('Az iskola adatok betöltése nem sikerült:', err))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || players.length === 0) return null;

  const handleChange = async (player: MyPlayerSchool, value: string) => {
    const schoolId = value ? Number(value) : null;
    setSavingId(player.playerId);
    try {
      await schoolsAPI.setMyPlayerSchool(player.playerId, schoolId);
      setPlayers((prev) =>
        prev.map((p) =>
          p.playerId === player.playerId
            ? { ...p, schoolId, schoolName: options.find((o) => o.id === schoolId)?.name ?? null }
            : p
        )
      );
      showToast('Iskola elmentve.');
    } catch (err: any) {
      showToast(getErrorMessage(err, 'Az iskolát nem sikerült elmenteni.'), 'error');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-gray-100 card-header bg-gradient-to-r from-amber-50 to-white px-4 py-4 md:px-6">
        <div className="flex items-center space-x-3">
          <div className="card-header-icon rounded-lg bg-amber-100 p-2">
            <AcademicCapIcon className="h-6 w-6 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Iskola</h2>
            <p className="text-sm text-gray-500">
              Ide küldheti a klub a kikérőket, igazolásokat - a levél másolatát te is megkapod.
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-4 p-4 md:p-6">
        {players.map((player) => (
          <div key={player.playerId} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
            <label htmlFor={`school-${player.playerId}`} className="text-sm font-medium text-gray-700 sm:w-48 sm:shrink-0">
              {player.playerName}
            </label>
            <select
              id={`school-${player.playerId}`}
              value={player.schoolId ?? ''}
              onChange={(e) => handleChange(player, e.target.value)}
              disabled={savingId === player.playerId}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
            >
              <option value="">Nincs megadva</option>
              {options.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        ))}
        <p className="text-xs text-gray-500">
          Ha nem találod az iskolát a listában, szólj az edzőnek, és felveszi.
        </p>
      </div>
    </div>
  );
};

export default MySchoolsCard;
